<script lang="ts">
  // A row/grid of activity statistics. Wraps onto more rows when there are more than `columns`.
  import type { Stat } from '../stats'
  import Diamonds from './Diamonds.svelte'
  import Icon from './Icon.svelte'
  import Shield from './Shield.svelte'

  export let stats: Stat[]
  export let columns = 4
  export let size: 'sm' | 'md' | 'lg' = 'md'
  export let icons = false
  export let dividers = true
  export let align: 'left' | 'center' = 'left'

  $: cols = Math.min(columns, stats.length)
</script>

{#if stats.length}
  <div class="xr-grid xr-{size}" class:xr-dividers={dividers} class:xr-center={align === 'center'} style="grid-template-columns: repeat({cols}, minmax(0, 1fr));">
    {#each stats as stat, i}
      <div class="xr-stat" class:xr-divided={dividers && i % cols !== 0}>
        <div class="xr-label">
          {#if icons}<Icon name={stat.icon} size={size === 'lg' ? 26 : 22} stroke={2.4} color="var(--accent)" />{/if}
          <span>{columns > 3 || stat.label.length > 13 ? stat.short : stat.label}</span>
        </div>
        {#if stat.parts}
          <div class="xr-value xr-parts">
            {#each stat.parts as part}
              <span class="xr-part">
                <span class="xr-dot" style="background: {part.color};" />
                {#if part.done}<Icon name="check" size={size === 'lg' ? 40 : 32} stroke={3} color="#5ca907" />{:else}{part.text}{/if}
              </span>
            {/each}
          </div>
        {:else}
          <div class="xr-value">
            {stat.value}{#if stat.unit && stat.unit !== 'XSS'}<span class="xr-unit">{stat.unit}</span>{/if}
            {#if stat.highest}<span class="xr-high"><Shield size={size === 'lg' ? 40 : size === 'sm' ? 32 : 36} /></span>{/if}
          </div>
        {/if}
        {#if stat.diamonds}
          <div class="xr-diamonds"><Diamonds value={stat.diamonds} size={size === 'lg' ? 24 : 20} color="var(--accent)" /></div>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<style>
  .xr-grid {
    display: grid;
    row-gap: 28px;
  }
  .xr-stat {
    min-width: 0;
    padding: 0 20px;
  }
  .xr-grid:not(.xr-center) .xr-stat {
    padding-left: 0;
  }
  .xr-stat.xr-divided {
    border-left: 2px solid var(--faint);
    padding-left: 24px;
  }
  .xr-center .xr-stat {
    text-align: center;
  }
  .xr-center .xr-label {
    justify-content: center;
  }
  .xr-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 21px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-value {
    margin-top: 6px;
    font-family: var(--display);
    font-weight: 700;
    font-size: 64px;
    line-height: 1;
    white-space: nowrap;
  }
  .xr-sm .xr-value {
    font-size: 52px;
  }
  .xr-lg .xr-value {
    font-size: 84px;
  }
  .xr-lg .xr-label {
    font-size: 23px;
  }
  .xr-high {
    display: inline-flex;
    margin-left: 10px;
    vertical-align: -0.04em;
  }
  .xr-diamonds {
    margin-top: 10px;
    display: flex;
  }
  .xr-center .xr-diamonds {
    justify-content: center;
  }
  /* Remaining Recovery: three figures, so smaller than a single value. */
  .xr-parts {
    display: flex;
    align-items: center;
    gap: 0.45em;
    font-size: 40px;
  }
  .xr-sm .xr-parts {
    font-size: 34px;
  }
  .xr-lg .xr-parts {
    font-size: 52px;
  }
  .xr-center .xr-parts {
    justify-content: center;
  }
  .xr-part {
    display: inline-flex;
    align-items: center;
    gap: 0.18em;
  }
  .xr-dot {
    width: 0.32em;
    height: 0.32em;
    border-radius: 50%;
    flex: none;
  }
  .xr-unit {
    margin-left: 6px;
    font-size: 0.5em;
    font-weight: 600;
    color: var(--muted);
  }
</style>
